import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, Info, AlertTriangle, ShieldCheck, Zap, HelpCircle, Monitor, Smartphone, Tv } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "O Que Você Precisa Saber Antes de Usar IPTV", href: "/blog/o-que-saber-antes-usar-iptv" }
  ];

  const faq = [
    {
      question: "IPTV funciona em qualquer internet?",
      answer: "Sim, desde que seja uma conexão banda larga estável. Evite internet via rádio com alta latência. Fibra óptica é o padrão ouro para IPTV."
    },
    {
      question: "Posso usar em dois lugares ao mesmo tempo?",
      answer: "Isso depende do seu plano. Na P2 PRO, temos opções de múltiplas telas que permitem o uso simultâneo em casas diferentes ou na mesma rede."
    },
    {
      question: "O sinal de IPTV atrasa em relação à TV aberta?",
      answer: "Existe um 'delay' natural do streaming (geralmente entre 15 a 30 segundos). No entanto, com a tecnologia P2P da P2 PRO, esse atraso é reduzido ao mínimo possível no mercado."
    }
  ];

  const benefits = [
    {
      title: "Planejamento Inteligente",
      description: "Saiba exatamente o que esperar e como preparar seu ambiente para o streaming.",
      icon: <Monitor className="w-6 h-6" />
    },
    {
      title: "Evite Gastos Desnecessários",
      description: "Não compre hardware que você não precisa. Entenda sua real necessidade técnica.",
      icon: <Zap className="w-6 h-6" />
    },
    {
      title: "Segurança de Dados",
      description: "Aprenda a identificar serviços confiáveis e proteja sua rede doméstica.",
      icon: <ShieldCheck className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Auditoria de Rede",
      description: "Verifique se sua internet suporta o tráfego de dados constante do IPTV."
    },
    {
      title: "Escolha do Player",
      description: "Selecione o aplicativo que melhor se adapta ao sistema do seu dispositivo."
    },
    {
      title: "Configuração DNS",
      description: "Aprenda por que mudar o DNS pode salvar sua experiência de streaming."
    },
    {
      title: "Teste de Estabilidade",
      description: "Realize testes em horários de pico antes de se comprometer com planos longos."
    }
  ];

  const diferenciais = [
    {
      title: "Know-how Técnico",
      description: "Dicas profissionais que você não encontra em tutoriais genéricos do YouTube.",
      icon: <Info className="w-6 h-6" />
    },
    {
      title: "Filtro de Qualidade",
      description: "Saiba separar o IPTV 'barato' que trava do IPTV profissional de alta performance.",
      icon: <ShieldCheck className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. A Verdade Sobre a Internet para IPTV",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Antes de apertar o play, você precisa entender que o <strong>IPTV</strong> não é como o YouTube ou a Netflix. No streaming sob demanda, o vídeo 'faz um buffer' (carrega um pedaço à frente). No IPTV ao vivo, o dado precisa chegar em tempo real. Se sua internet oscilar por 1 segundo, a imagem trava. Por isso, ter uma banda larga de qualidade é o requisito número zero.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Não se iluda apenas com o número de 'Megas' que sua operadora vende. O que importa para a <strong>P2 PRO</strong> é a <em>estabilidade da rota</em> e a latência (ping). Neste guia, revelamos o que as operadoras não contam sobre o tráfego de vídeo e como você pode preparar sua rede para não passar raiva durante o jogo do seu time.
          </p>
        </div>
      )
    },
    {
      title: "2. Hardware: Onde a Mágica Acontece",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Tv className="w-6 h-6 text-electric" /> Smart TVs Antigas</h4>
              <p className="text-sm text-muted-foreground">Se sua TV tem mais de 4 anos, o processador dela pode sofrer para decodificar listas modernas de 4K. Considere um hardware externo.</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Smartphone className="w-6 h-6 text-amber-500" /> Mobilidade Total</h4>
              <p className="text-sm text-muted-foreground">IPTV no celular consome muita bateria e dados móveis. Esteja ciente disso antes de usar seu 4G/5G para maratonar séries.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. O Perigo das Listas Gratuitas e Serviços 'Baratinhos'",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">O barato sai caro. Entenda os riscos de não escolher um serviço profissional como a P2 PRO:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Segurança da sua Rede</h4>
              <p className="text-muted-foreground">Serviços obscuros podem conter malwares que monitoram o tráfego da sua rede doméstica. Use sempre aplicativos homologados.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Quedas em Grandes Eventos</h4>
              <p className="text-muted-foreground">Servidores amadores caem justamente quando você mais precisa (final de campeonato ou estreia de série). A infraestrutura de elite garante o sinal no ar 99.9% do tempo.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Checklist de Configuração Profissional",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Antes de iniciar sua jornada, certifique-se de ter cumprido estes pontos técnicos:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">DNS do Google ou Cloudflare</h5>
                <p className="text-sm text-muted-foreground">Configure 8.8.8.8 ou 1.1.1.1 no seu roteador. Isso evita bloqueios de operadoras e melhora a velocidade de conexão.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Cabo Cat5e ou Superior</h5>
                <p className="text-sm text-muted-foreground">Se for usar cabo, certifique-se de que ele não está com as pontas oxidadas. Um cabo ruim limita sua rede a 10Mbps.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Cuidado com o Superaquecimento</h5>
                <p className="text-sm text-muted-foreground">TV Boxes baratas esquentam muito e começam a travar o vídeo após 30 minutos de uso. Mantenha seu dispositivo em local ventilado.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias IPTV"
        title="O Que Você Precisa Saber Antes de Usar IPTV em 2026"
        subtitle="Evite frustrações e gastos errados. Descubra os requisitos técnicos reais para um streaming de alta performance."
        heroImage="https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "Muitas pessoas entram no mundo do IPTV atraídas pela promessa de milhares de canais por um preço baixo, mas acabam se frustrando porque não se prepararam tecnicamente. O IPTV é uma tecnologia fantástica, mas exige que você entenda como ela interage com seu hardware e sua internet. Não é apenas 'comprar uma lista', é construir um ecossistema de entretenimento na sua casa.",
          "Neste guia denso, vamos abordar tudo o que os vendedores comuns não te dizem. Desde a importância da rota de internet até os limites do processamento das Smart TVs modernas. Na P2 PRO, acreditamos que um cliente bem informado é um cliente satisfeito, pois ele sabe extrair o máximo do potencial que nossa infraestrutura oferece.",
          "Prepare-se para uma aula técnica simplificada. Vamos passar pelos pontos críticos que definem se sua experiência será um sonho cinematográfico ou um pesadelo de travamentos constantes."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="💡 Quer Começar do Jeito Certo?"
        ctaDescription="A equipe da P2 PRO está pronta para realizar uma auditoria na sua conexão e recomendar o melhor setup para sua casa."
        ctaButtonText="Falar com Especialista"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/o-que-saber-antes-usar-iptv")({
  head: () => ({
    meta: [
      { title: "O Que Saber Antes de Usar IPTV: Guia Técnico 2026" },
      { name: "description", content: "Requisitos essenciais para IPTV: internet, hardware, DNS e segurança. Tudo o que você precisa conferir antes de assinar qualquer serviço de streaming." },
    ],
  }),
  component: ArticlePage,
});
